import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, DataTable, Select, Switch, useSelect, useSwitch } from '@chilecompra/react-kit/components'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import truncate from '../../modules/utils/limiter'
import generateYearsBetween from '../../modules/utils/years'
import TreemapChart from '../../components/Charts/TreemapChart'
import RegionalItemsAndProducts from '../../components/Infographics/RegionalItemsAndProducts'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { onGetItemsAndProductsThunk } from './ItemsAndProductsSection.actions'
import {
  CategoryIcon,
  Content,
  FamilyIcon,
  GridContainer,
  GridItem,
  ProductIcon,
  Root,
  SegmentIcon
} from './ItemsAndProductsSection.styles'

const NEXT_TYPES_DICTIONARY = {
  categories: 'products',
  families: 'categories',
  initial: 'segments',
  segments: 'families'
}

const TYPE_ICONS_DICTIONARY = {
  categories: <CategoryIcon />,
  families: <FamilyIcon />,
  products: <ProductIcon />,
  segments: <SegmentIcon />
}

/**
 * The ItemsAndProductsSection's container.
 */
const ItemsAndProductsSection = () => {
  const dispatch = useDispatch()
  const { endYear, latestUpdate, loading, startYear } = useSelector(state => state.topNavbar)
  const { csv, data } = useSelector(state => state.itemsAndProductsSection)
  const [levels, setLevels] = useState([])
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => {
      dispatch(
        onGetItemsAndProductsThunk({
          type: NEXT_TYPES_DICTIONARY.initial,
          year: updatedValue,
          callback: () => setLevels([])
        })
      )
    },
    overwriteCallback: () => endYear
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('items-and-products-treemap-chart', `Info_Region_Rubros_Productos_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Region_Rubros_Productos_${yearValue}`)
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const handleChartLabelFormatter = useCallback(
    function () {
      return `
        <div style="position: relative; white-space: initial; width: 100%;">
          ${this.point.value >= 8 ? this.point.label : truncate(this.point.label, 45, ' ...')}
          <br />
          
          <strong>
          ${currencyValue ? CURRENCY_VALUES.USD : CURRENCY_VALUES.CLP}${formatAmount(
            currencyValue ? this.point.amountUSD : this.point.amount
          )}
          </strong>
        </div>
      `
    },
    [currencyValue]
  )
  const handleClickMap = useCallback(
    (type, label, categoryId, familyId, productId, segmentId) => {
      if (['categories', 'families', 'segments'].indexOf(type) === -1) {
        return
      }

      dispatch(
        onGetItemsAndProductsThunk({
          categoryId,
          familyId,
          productId,
          segmentId,
          type: NEXT_TYPES_DICTIONARY[type],
          year: yearValue,
          callback: () =>
            setLevels(currentLevels => [
              ...currentLevels,
              {
                categoryId,
                familyId,
                productId,
                segmentId,
                icon: TYPE_ICONS_DICTIONARY[NEXT_TYPES_DICTIONARY[type]],
                name: label,
                type: NEXT_TYPES_DICTIONARY[type]
              }
            ])
        })
      )
    },
    [yearValue]
  )
  const handleClickTree = useCallback(
    (item, index) => {
      setLevels(currentCollection => currentCollection.slice(0, index + 1))
      dispatch(
        onGetItemsAndProductsThunk({
          categoryId: item.categoryId,
          familyId: item.familyId,
          segmentId: item.segmentId,
          year: yearValue,
          type: item.type
        })
      )
    },
    [yearValue]
  )
  const handleReset = useCallback(() => {
    setLevels([])
    dispatch(onGetItemsAndProductsThunk({ type: NEXT_TYPES_DICTIONARY.initial, year: yearValue }))
  }, [yearValue])

  useEffect(() => {
    if (!yearValue) {
      return
    }

    dispatch(onGetItemsAndProductsThunk({ type: NEXT_TYPES_DICTIONARY.initial, year: yearValue }))
  }, [yearValue])

  return (
    <Root>
      <Content>
        <GridContainer>
          <GridItem xs={12}>
            <DataLayout
              disableCaptureImage={disableCaptureImage}
              disableDownloadFile={disableDownloadFile}
              header={
                <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                  <GridItem md={2} xs={12}>
                    <Select
                      label="AÑO"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem md={2} xs={12}>
                    <Switch
                      checked={currencyValue}
                      color="primary"
                      labelLeft="CLP"
                      labelRight="USD"
                      margin="0 0 0 30px"
                      onChange={handleChangeCurrency}
                    />
                  </GridItem>
                </GridContainer>
              }
              infographic={<RegionalItemsAndProducts />}
              noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
              ref={dataLayoutRef}
              updatedDate={latestUpdate}
              onCaptureImage={handleCaptureImage}
              onChangeView={handleChangeView}
              onDownloadFile={handleDownloadFile}
            >
              <DataLayoutChild view={DataLayoutViews.CHART}>
                <Card color="blank" elevation="1" margin="0 0 32px">
                  <GridContainer>
                    <GridItem xs={12}>
                      <TreemapChart
                        data={data}
                        id="items-and-products-treemap-chart"
                        levels={levels}
                        rootIcon={<SegmentIcon />}
                        rootName="Estado de Chile"
                        labelFormatter={handleChartLabelFormatter}
                        onClickMap={handleClickMap}
                        onClickTree={handleClickTree}
                        onReset={handleReset}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </DataLayoutChild>
              <DataLayoutChild view={DataLayoutViews.TABLE}>
                <Card color="blank" elevation="1" margin="0 0 32px">
                  <GridContainer padding="0 0 24px">
                    <GridItem xs={12}>
                      <DataTable
                        headers={[
                          { label: 'Nombre rubro', value: 'label' },
                          { label: 'Monto total (CLP)', value: 'amount' },
                          { label: 'Monto total (USD)', value: 'amountUSD' }
                        ]}
                        headersCellsProps={[
                          { enableSort: true, width: '60%' },
                          { enableSort: true },
                          { enableSort: true }
                        ]}
                        initialRows={6}
                        loading={loading}
                        records={data}
                        recordsDecorators={[
                          ({ label }) => label,
                          ({ amount }) => `$${formatAmount(amount)}`,
                          ({ amountUSD }) => `$${formatAmount(amountUSD)}`
                        ]}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </DataLayoutChild>
            </DataLayout>
          </GridItem>
        </GridContainer>
      </Content>
    </Root>
  )
}

export default ItemsAndProductsSection
